import { createContext, ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { basicLogger, createClient } from '@launchdarkly/react-sdk';
import * as Sentry from '@sentry/react';
import { useAppSelector } from 'hooks';
import { trackPendoEvent } from 'services/PendoService';

import {
  FEATURE_FLAG_DEFAULTS,
  FEATURE_FLAG_PATHS,
  FEATURE_FLAGS,
  FeatureFlagName,
  FeatureFlagPath,
  FeatureFlagSurface,
  FeatureFlagValues,
  normalizeGeofencePath
} from './flags';
import { parseHeadsUpIntroConfig } from './headsUpIntroConfig';
import { parseMultiRunIntroConfig } from './multiRunIntroConfig';

interface FeatureFlagContextValue {
  values: FeatureFlagValues;
  evaluated: boolean;
  /** Whose evaluation `values` are; a member's playerGUID, or the anonymous identity. */
  evaluatedIdentity: string | null;
}

const FeatureFlagContext = createContext<FeatureFlagContextValue>({
  values: FEATURE_FLAG_DEFAULTS,
  evaluated: false,
  evaluatedIdentity: null
});

/**
 * How long to wait for LaunchDarkly before giving up and keeping the defaults.
 *
 * RAISED FROM 2s AFTER EN-11063. Two seconds is a desktop number. A cold start
 * in the Android WebView -- fresh TLS to LaunchDarkly, no warmed connection, a
 * mobile network -- routinely exceeds it, and the SDK does not reject when it
 * does (see the `status` handling below), so the whole flag set silently pinned
 * to its compiled defaults while reporting itself ready. On iOS the same build
 * finished inside 2s and read real values, which is exactly the platform split
 * QA reported: same flag, same environment, opposite geolocation path.
 *
 * The cost of raising it is bounded and paid only when LaunchDarkly is slow:
 * `useFeatureFlagsReady` settles at the ceiling below regardless, so nothing
 * can wait longer than this plus a little.
 */
const INITIALIZATION_TIMEOUT_SECONDS = 5;

// The evaluation identity of a visitor who is not logged in. Never sent to
// LaunchDarkly: a guest's context is anonymous with an empty key, which the SDK
// replaces with a generated key kept in local storage. A returning guest stays
// in the same rollout bucket and counts as one context, not one per page load.
const ANONYMOUS_IDENTITY = 'anonymous';

const buildEnvironmentAttributes = () => ({
  platform: Capacitor.getPlatform(),
  environment: process.env.REACT_APP_ENVIRONMENT || process.env.NODE_ENV,
  bundleVersion: process.env.REACT_APP_VERSION || 'dev'
});

const buildContext = (identity: string, contextName: string) =>
  identity === ANONYMOUS_IDENTITY
    ? { kind: 'user' as const, key: '', anonymous: true, ...buildEnvironmentAttributes() }
    : {
        kind: 'user' as const,
        key: identity,
        ...(contextName ? { name: contextName } : {}),
        ...buildEnvironmentAttributes()
      };

/**
 * "Are this player's flags known yet?" — for the rare consumer that must not
 * act on the safe defaults while an evaluation is still in flight (a login
 * modal deciding whether a flagged announcement takes the session's slot,
 * EN-10791). It never blocks rendering, and it is bounded:
 *   - true whenever the provider will not evaluate at all (no client-side ID,
 *     a session whose member profile has not hydrated yet, or the SDK failed
 *     to construct);
 *   - true once the SDK's `start()` settles for the current identity: the
 *     server's payload arrived, or the SDK gave up waiting and is answering
 *     from its local cache. NOT as soon as a cached value is read: that cache
 *     is what the player was served on their last visit, and a consumer that
 *     commits to it (the Lobby pins the geolocation path for the login) would
 *     keep a flag change the operator already made away from every returning
 *     player for one more session;
 *   - true at a ceiling just past the SDK's own initialization timeout, so a
 *     `start()` that never settles cannot hold a consumer forever.
 * Outside a provider (tests, stories) it reads true, like the defaults do.
 */
const FeatureFlagReadyContext = createContext<boolean>(true);
const READY_CEILING_MS = INITIALIZATION_TIMEOUT_SECONDS * 1000 + 500;

const readClientSideId = (): string | undefined =>
  process.env.REACT_APP_LAUNCHDARKLY_CLIENT_SIDE_ID?.trim();

// What a JSON flag's parser reports about a malformed payload: the field and
// why, never the payload (multiRunIntroConfig, headsUpIntroConfig).
type PayloadIssueReporter = (issue: { field: string; reason: string }) => void;

// One reporter per payload flag, keyed by the flag's key, so a Sentry message
// names the flag whose payload is wrong.
type PayloadIssueReporters = { [Flag in PayloadFlagName]: PayloadIssueReporter };

// Reports a malformed payload once per field and reason for a client's
// lifetime: the field has already fallen back (see multiRunIntroConfig), and
// the operator needs one signal, not one per re-evaluation. Only the field and
// the reason go out, never the payload.
const createPayloadIssueReporter = (flagKey: string): PayloadIssueReporter => {
  const reported = new Set<string>();
  return ({ field, reason }) => {
    const id = `${field}:${reason}`;
    if (reported.has(id)) return;
    reported.add(id);
    Sentry.captureMessage(`Invalid ${flagKey} payload: ${field}`, {
      level: 'warning',
      tags: { flag: flagKey, field },
      extra: { reason }
    });
  };
};

type LDClient = ReturnType<typeof createClient>;

type BooleanFlagName = {
  [Flag in FeatureFlagName]: FeatureFlagValues[Flag] extends boolean ? Flag : never;
}[FeatureFlagName];

/** The JSON flags whose payload is parsed on the way in. */
type PayloadFlagName = 'multiRunIntro' | 'headsUpIntro';
const PAYLOAD_FLAG_NAMES: readonly PayloadFlagName[] = ['multiRunIntro', 'headsUpIntro'];

const createPayloadIssueReporters = (): PayloadIssueReporters =>
  Object.fromEntries(
    PAYLOAD_FLAG_NAMES.map((flag) => [flag, createPayloadIssueReporter(FEATURE_FLAGS[flag])])
  ) as PayloadIssueReporters;

const readBool = (flag: BooleanFlagName) => (client: LDClient) =>
  client.boolVariation(FEATURE_FLAGS[flag], FEATURE_FLAG_DEFAULTS[flag]);

/**
 * How each flag is read from the SDK. Every `*Variation` call is an evaluation
 * LaunchDarkly counts, so a flag is read only when it is first evaluated or
 * when the SDK reports that it changed -- never because a different flag did.
 */
const FLAG_READERS: {
  [Flag in FeatureFlagName]: (
    client: LDClient,
    reportIssue: PayloadIssueReporters
  ) => FeatureFlagValues[Flag];
} = {
  reduceAnimations: readBool('reduceAnimations'),
  sngPayoutStructure: readBool('sngPayoutStructure'),
  performanceHud: readBool('performanceHud'),
  systemLog: readBool('systemLog'),
  multiRunIntro: (client, reportIssue) =>
    parseMultiRunIntroConfig(
      client.jsonVariation(FEATURE_FLAGS.multiRunIntro, FEATURE_FLAG_DEFAULTS.multiRunIntro),
      reportIssue.multiRunIntro
    ),
  headsUpIntro: (client, reportIssue) =>
    parseHeadsUpIntroConfig(
      client.jsonVariation(FEATURE_FLAGS.headsUpIntro, FEATURE_FLAG_DEFAULTS.headsUpIntro),
      reportIssue.headsUpIntro
    ),
  multiRunWithhold3x: readBool('multiRunWithhold3x'),
  sitgoSeatToggle: readBool('sitgoSeatToggle'),
  sitGoSeatedWaiting: readBool('sitGoSeatedWaiting'),
  sitGoResultModal: readBool('sitGoResultModal'),
  sitGoBuyInLobby: readBool('sitGoBuyInLobby'),
  // Normalized on the way in, so every consumer sees one of the three known
  // paths. A string flag holds whatever an operator typed; an unrecognised
  // value becomes the compiled default, which is safe under either server
  // setting (see normalizeGeofencePath).
  geofencePath: (client) =>
    normalizeGeofencePath(
      client.stringVariation(FEATURE_FLAGS.geofencePath, FEATURE_FLAG_DEFAULTS.geofencePath)
    ),
  tournamentAddOnInfo: readBool('tournamentAddOnInfo'),
  referrals: readBool('referrals'),
  preactionCheckboxIndicator: readBool('preactionCheckboxIndicator'),
  lobbyCashInPlay: readBool('lobbyCashInPlay'),
  boardMissingRecovery: readBool('boardMissingRecovery'),
  tournamentRecurringSchedule: readBool('tournamentRecurringSchedule'),
  frameOrderReport: readBool('frameOrderReport'),
  tableFrameOrder: readBool('tableFrameOrder')
};

const ALL_FLAG_NAMES = Object.keys(FEATURE_FLAGS) as FeatureFlagName[];

const FLAG_NAMES_BY_KEY = new Map<string, FeatureFlagName>(
  ALL_FLAG_NAMES.map((flagName) => [FEATURE_FLAGS[flagName], flagName])
);

/** `previous` with the named flags re-read from the SDK; the rest are kept. */
const readValues = (
  client: LDClient,
  reportIssue: PayloadIssueReporters,
  flagNames: FeatureFlagName[],
  previous: FeatureFlagValues
): FeatureFlagValues =>
  flagNames.reduce<FeatureFlagValues>(
    (values, flagName) => ({
      ...values,
      [flagName]: FLAG_READERS[flagName](client, reportIssue)
    }),
    previous
  );

const describeValue = (flagName: FeatureFlagName, values: FeatureFlagValues): FeatureFlagPath =>
  (FEATURE_FLAG_PATHS[flagName] as (value: FeatureFlagValues[FeatureFlagName]) => FeatureFlagPath)(
    values[flagName]
  );

const recordEvaluation = (values: FeatureFlagValues) => {
  for (const flagName of Object.keys(values) as FeatureFlagName[]) {
    const { variant, path: selectedPath } = describeValue(flagName, values);
    const flagKey = FEATURE_FLAGS[flagName];

    // LaunchDarkly records the evaluation/cohort. Sentry records the selected
    // path beside the existing bundle-version tag so subsequent client errors
    // can be correlated without attaching player identity or other PII.
    Sentry.setTag(`flag.${flagName}`, variant);
    Sentry.setTag(`path.${flagName}`, selectedPath);
    Sentry.addBreadcrumb({
      category: 'feature-flag',
      message: flagKey,
      level: 'info',
      data: {
        variant,
        selectedPath,
        bundleVersion: process.env.REACT_APP_VERSION || 'dev'
      }
    });
  }
};

/**
 * `guestSession` is the token-backed session (useGuest), passed in rather than
 * read here: the auth module imports the store, and every flagged component's
 * tests load this module, so importing it would load the whole app with them.
 * SessionFeatureFlagProvider supplies it at the app root.
 */
export const FeatureFlagProvider = ({
  children,
  guestSession
}: {
  children: ReactNode;
  guestSession: boolean;
}) => {
  const playerGUID = useAppSelector((state) => state.user.playerGUID);
  const playerName = useAppSelector((state) => state.user.playerName);
  const displayName = useAppSelector((state) => state.user.displayName);
  const guest = useAppSelector((state) => state.user.guest);
  // The token-backed session is authoritative. Redux starts every visitor as a
  // guest until the profile hydrates, so a member's boot is neither evaluated
  // as a guest nor at all until their GUID lands; and when a session ends,
  // Redux still holds the previous player until the reload, so that player's
  // flags must not outlive the session.
  const memberIdentity = !guestSession && !guest && playerGUID ? playerGUID : null;
  const identity = guestSession ? ANONYMOUS_IDENTITY : memberIdentity;
  const publicName = displayName?.trim() || playerName?.trim() || '';
  // Newer identity records may store the Descope subject/GUID in the legacy
  // username column. Do not duplicate that opaque identifier as the console
  // label when no public poker display name is available.
  const contextName = publicName && publicName !== playerGUID ? publicName : '';
  const [evaluation, setEvaluation] = useState<{
    identity: string;
    values: FeatureFlagValues;
  } | null>(null);
  // The identity whose `start()` has finished: the server answered, it timed
  // out (leaving the SDK's cache as the best answer there is), it failed, the
  // SDK could not be constructed, or the ceiling passed.
  const [settledIdentity, setSettledIdentity] = useState<string | null>(null);
  const settled = settledIdentity === identity;
  const hasValues = evaluation?.identity === identity;
  const values = hasValues ? evaluation.values : FEATURE_FLAG_DEFAULTS;
  // Values can land from the SDK's local cache before the server answers, and
  // that cache holds whatever this player was served on their LAST visit. So
  // they render, but they are not this player's evaluation -- nothing commits
  // to them, and no exposure is recorded for them -- until `start()` settles.
  const evaluated = hasValues && settled;
  const evaluatedIdentity = evaluated ? identity : null;
  const contextValue = useMemo(
    () => ({ values, evaluated, evaluatedIdentity }),
    [evaluated, evaluatedIdentity, values]
  );
  const latestContextNameRef = useRef(contextName);
  latestContextNameRef.current = contextName;
  const clientRef = useRef<{
    identity: string;
    contextName: string;
    client: LDClient;
  } | null>(null);
  const ready = !readClientSideId() || !identity || settled;

  useEffect(() => {
    // Start every identity from nothing. Both pieces of state are keyed by the
    // GUID alone, so a player who logs out and back in would otherwise read
    // "ready" — with the previous client's values, for a flag that may have
    // changed since — while the new client's start() is still pending.
    setEvaluation(null);
    setSettledIdentity(null);
    // Replace rollout telemetry as soon as an identity changes so later errors
    // cannot be attributed to the previous player's cohort.
    recordEvaluation(FEATURE_FLAG_DEFAULTS);

    const clientSideId = readClientSideId();
    if (!clientSideId || !identity) return;

    let client: LDClient;
    try {
      client = createClient(clientSideId, buildContext(identity, latestContextNameRef.current), {
        logger: basicLogger({ level: 'warn' })
      });
    } catch {
      // Invalid/missing runtime configuration must not prevent the game loading.
      setSettledIdentity(identity);
      return;
    }
    let active = true;
    const settle = () => {
      if (active) setSettledIdentity(identity);
    };
    const readyCeiling = window.setTimeout(settle, READY_CEILING_MS);
    const reportIssue = createPayloadIssueReporters();

    // This client's latest values, and how they were last brought up to date.
    let values: FeatureFlagValues = FEATURE_FLAG_DEFAULTS;
    let evaluatedAll = false;
    let receivedPayload = false;

    const syncValues = (flagNames: FeatureFlagName[]) => {
      if (!active || flagNames.length === 0) return;
      values = readValues(client, reportIssue, flagNames, values);
      setEvaluation({ identity, values });
      recordEvaluation(values);
    };
    const syncAllValues = () => {
      evaluatedAll = true;
      syncValues(ALL_FLAG_NAMES);
    };

    // One listener for the whole batch, and only the flags in it are re-read.
    // A per-flag `change:<key>` listener that re-read every flag turned the
    // initial payload -- which the SDK reports as a change to every key, from
    // its local cache and again from the server -- into flags² evaluations.
    // Subscribing to `change` is also what keeps the streaming connection open.
    const onChange = (_context: unknown, changedKeys: string[]) => {
      if (!active) return;
      receivedPayload = true;
      const changedFlags = changedKeys.flatMap((key) => FLAG_NAMES_BY_KEY.get(key) ?? []);
      if (evaluatedAll) {
        syncValues(changedFlags);
      } else {
        // The first sync for this identity must cover every flag, so that
        // `evaluated` never publishes defaults for flags that were not read.
        syncAllValues();
      }
    };
    clientRef.current = {
      identity,
      contextName: latestContextNameRef.current,
      client
    };
    client.on('change', onChange);

    // A late initialization still publishes real values. The first payload,
    // cached or from the server, normally arrives through `change` above; this
    // is the backstop for one that does not, after a `start()` that timed out
    // with nothing cached. Once a payload has been read it would only repeat it.
    const onInitialized = () => {
      if (!receivedPayload) syncAllValues();
    };
    client.on('initialized', onInitialized);

    // App rendering never waits for LaunchDarkly. Defaults remain active until
    // initialization completes, and an unavailable service leaves them intact.
    void client
      .start({ timeout: INITIALIZATION_TIMEOUT_SECONDS })
      // Deliberately reads the values whatever the status. `start()` RESOLVES
      // on timeout rather than rejecting, and on a timeout the SDK still
      // answers from its own localStorage cache of this context's last
      // evaluation -- a real answer for this player, and a far better one than
      // the compiled defaults. Only a context that has never been evaluated on
      // this device falls all the way back. Skipped when a payload has already
      // been read, which is the normal case.
      .then(() => {
        if (!evaluatedAll) syncAllValues();
      })
      .catch(() => {
        // Safe defaults remain active. The SDK logger reports the failure.
      })
      .finally(settle);

    return () => {
      active = false;
      window.clearTimeout(readyCeiling);
      client.off('initialized', onInitialized);
      client.off('change', onChange);
      if (clientRef.current?.client === client) clientRef.current = null;
      void client.close();
    };
  }, [identity]);

  useEffect(() => {
    const current = clientRef.current;
    if (
      !memberIdentity ||
      !current ||
      current.identity !== memberIdentity ||
      current.contextName === contextName
    )
      return;

    // Profile data can hydrate after authentication. Enrich the existing
    // context instead of tearing down and reopening the streaming connection.
    // Any flag the richer context evaluates differently arrives through the
    // client's `change` listener, so there is nothing to re-read here.
    current.contextName = contextName;
    void current.client
      .identify(buildContext(memberIdentity, contextName), { sheddable: true })
      .catch(() => {
        // Keep the last evaluation. The SDK logger reports the failure and a
        // later profile update can retry enrichment without disrupting flags.
      });
  }, [contextName, memberIdentity]);

  return (
    <FeatureFlagContext.Provider value={contextValue}>
      <FeatureFlagReadyContext.Provider value={ready}>{children}</FeatureFlagReadyContext.Provider>
    </FeatureFlagContext.Provider>
  );
};

export const useFeatureFlag = <Flag extends FeatureFlagName>(flag: Flag): FeatureFlagValues[Flag] =>
  useContext(FeatureFlagContext).values[flag];

export const useFeatureFlagsReady = (): boolean => useContext(FeatureFlagReadyContext);

/**
 * Record the selected path when a player reaches the UI boundary controlled by
 * a flag. Evaluating a flag at app startup is not itself a product exposure.
 */
export const useFeatureFlagExposure = (
  flag: FeatureFlagName,
  surface: FeatureFlagSurface,
  active = true
): void => {
  const { values, evaluated, evaluatedIdentity } = useContext(FeatureFlagContext);
  // Null-safe: the lobby records an exposure too (EN-11139), and is rendered
  // against partial stores. No player simply records nothing.
  const playerGUID = useAppSelector((state) => state.user?.playerGUID);
  const guest = useAppSelector((state) => state.user?.guest);
  const { variant, path: selectedPath } = describeValue(flag, values);

  useEffect(() => {
    if (!active || !evaluated || guest || !playerGUID) return;
    // The values must be this player's own evaluation. At logout the token
    // session turns anonymous before Redux clears the member, and an anonymous
    // evaluation must not be recorded as the previous player's cohort.
    if (evaluatedIdentity !== playerGUID) return;

    trackPendoEvent({
      playerGUID,
      eventName: 'feature_flag_exposure',
      dedupeKey: `${playerGUID}:${FEATURE_FLAGS[flag]}:${variant}`,
      properties: {
        flag_key: FEATURE_FLAGS[flag],
        variation: variant,
        selected_path: selectedPath,
        surface
      }
    });
  }, [
    active,
    evaluated,
    evaluatedIdentity,
    flag,
    guest,
    playerGUID,
    selectedPath,
    surface,
    variant
  ]);
};
